import { Link, useLocation } from 'react-router-dom';
import { ShoppingBag, Heart, User, Menu, X, Search, ArrowUp } from 'lucide-react';
import { useState, useEffect, useMemo, lazy, Suspense } from 'react';
import { motion, AnimatePresence, useScroll, useSpring, useMotionValue, useReducedMotion, useMotionValueEvent } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { useBrands } from '../context/BrandContext';
import { useUI } from '../context/UIContext';
import { useCurrency } from '../context/CurrencyContext';
import LollipopLogo from '../assets/lollipop-logo.svg';

const CartDrawer = lazy(() => import('./CartDrawer').then(m => ({ default: m.CartDrawer })));
const SearchOverlay = lazy(() => import('./SearchOverlay').then(m => ({ default: m.SearchOverlay })));
const RecentlyViewed = lazy(() => import('./RecentlyViewed').then(m => ({ default: m.RecentlyViewed })));

const ScrollProgressBar = ({ themeColor }) => {
  const { scrollYProgress } = useScroll();
  const shouldReduceMotion = useReducedMotion();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: shouldReduceMotion ? 1000 : 100,
    damping: shouldReduceMotion ? 100 : 30,
    restDelta: 0.001
  });

  const [scrollPercentage, setScrollPercentage] = useState(0);

  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    setScrollPercentage(Math.round(latest * 100));
  });

  return (
    <motion.div
      className="absolute top-0 left-0 right-0 h-1 z-[60] origin-left"
      style={{
        scaleX,
        backgroundColor: themeColor
      }}
      role="progressbar"
      aria-label="Scroll progress"
      aria-valuemin="0"
      aria-valuemax="100"
      aria-valuenow={scrollPercentage}
    />
  );
};

const CustomCursor = () => {
  const { isKeyboardUser } = useUI();
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const springConfig = { damping: 20, stiffness: 250, mass: 0.5 };
  const x = useSpring(mouseX, springConfig);
  const y = useSpring(mouseY, springConfig);

  const [isHovering, setIsHovering] = useState(false);
  const [cursorType, setCursorType] = useState('default');
  const [cursorText, setCursorText] = useState('');
  const shouldReduceMotion = useReducedMotion();

  useEffect(() => {
    if (shouldReduceMotion || window.innerWidth < 1024) return;

    const handleMouseMove = (e) => {
      mouseX.set(e.clientX - 16);
      mouseY.set(e.clientY - 16);
    };

    const handleMouseOver = (e) => {
      const target = e.target;
      const clickable = target.closest('a, button') ||
                       (window.getComputedStyle(target).cursor === 'pointer' ? target : null);

      const isClickable = !!clickable;
      setIsHovering(isClickable);

      if (isClickable) {
        const text = clickable.getAttribute('data-cursor') ||
                     clickable.closest('[data-cursor]')?.getAttribute('data-cursor');
        setCursorText(text || '');
      } else {
        setCursorText('');
      }

      // Check for brand sections
      if (target.closest('.btn-popsicle') || target.closest('[data-testid*="popsicle"]')) {
        setCursorType('popsicle');
      } else if (target.closest('.btn-lollipop') || target.closest('[data-testid*="lollipop"]')) {
        setCursorType('lollipop');
      } else {
        setCursorType('default');
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseover', handleMouseOver);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseover', handleMouseOver);
    };
  }, [mouseX, mouseY, shouldReduceMotion]);

  if (shouldReduceMotion || isKeyboardUser) return null;

  return (
    <motion.div
      className="fixed top-0 left-0 w-8 h-8 pointer-events-none z-[9999] hidden lg:block"
      aria-hidden="true"
      style={{
        x,
        y,
        scale: isHovering ? 1.5 : 1,
      }}
    >
      <div
        className={`w-full h-full rounded-full border-2 border-black transition-colors duration-300 flex items-center justify-center ${
          cursorType === 'popsicle' ? 'bg-[#00FFFF]' :
          cursorType === 'lollipop' ? 'bg-[#FFC1CC]' :
          'bg-[#FFF44F]'
        }`}
        style={{
          boxShadow: '2px 2px 0px 0px #000000',
        }}
      >
        {isHovering && (cursorType !== 'default' || cursorText) && (
          <motion.span
            key={cursorText || 'view'}
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-[8px] font-black uppercase"
          >
            {cursorText || 'View'}
          </motion.span>
        )}
      </div>
    </motion.div>
  );
};

export const Layout = ({ children }) => {
  const shouldReduceMotion = useReducedMotion();
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();
  const { itemCount, setIsOpen: setCartOpen } = useCart();
  const { wishlist } = useWishlist();
  const { brands } = useBrands();
  const { setIsKeyboardUser } = useUI();
  const { currency, setCurrency } = useCurrency();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [settings, setSettings] = useState({
    bannerText: process.env.REACT_APP_BANNER_TEXT || 'FREE SHIPPING ON ORDERS OVER $75',
    logoText: process.env.REACT_APP_LOGO_TEXT || 'pop.boutique'
  });

  const { scrollYProgress } = useScroll();

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Tab') {
        setIsKeyboardUser(true);
        document.body.classList.add('is-keyboard-user');
      }
    };

    const handleMouseDown = () => {
      setIsKeyboardUser(false);
      document.body.classList.remove('is-keyboard-user');
    };

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('mousedown', handleMouseDown);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, [setIsKeyboardUser]);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Build dynamic nav links from brands
  const navLinks = [
    { path: '/shop', label: 'Shop All' },
    ...brands.map(brand => ({
      path: `/shop?brand=${brand.slug}`,
      label: brand.name
    })),
    { path: '/about', label: 'About' },
  ];

  // Build brand names for marquee
  const brandNames = brands.map(b => b.name).join(' × ');

  // Determine theme color for progress bar
  const themeColor = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const brandSlug = params.get('brand');
    if (brandSlug) {
      const brand = brands.find(b => b.slug === brandSlug);
      return brand?.colors?.primary || '#00FFFF';
    }
    if (location.pathname.startsWith('/product/')) {
      return '#FFC1CC';
    }
    return '#00FFFF';
  }, [location, brands]);

  return (
    <div className={`min-h-screen flex flex-col ${shouldReduceMotion ? '' : 'lg:cursor-none'}`}>
      <CustomCursor />
      {/* Skip to content */}
      <a
        href="#main-content"
        className="skip-to-content"
      >
        Skip to content
      </a>

      {/* Announcement Bar */}
      <div className="bg-black text-white py-2 overflow-hidden relative group" role="region" aria-label="Promotions">
        <div className="sr-only">
          {settings.bannerText} ★ VINTAGE TEES FROM THE VAULT ★ {brandNames}
        </div>
        <div
          className={`animate-marquee whitespace-nowrap flex ${isPaused ? 'pause-animation' : ''}`}
          aria-hidden="true"
        >
          {[...Array(5)].map((_, i) => (
            <span key={i} className="flex">
              <span className="mx-8 font-body text-sm shimmer-text font-bold">{settings.bannerText}</span>
              <span className="mx-8 font-body text-sm">★</span>
              <span className="mx-8 font-body text-sm">VINTAGE TEES FROM THE VAULT</span>
              <span className="mx-8 font-body text-sm">★</span>
              <span className="mx-8 font-body text-sm">{brandNames}</span>
              <span className="mx-8 font-body text-sm">★</span>
            </span>
          ))}
        </div>
        <button
          onClick={() => setIsPaused(!isPaused)}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/20 p-1 rounded-full transition-colors opacity-0 group-hover:opacity-100 focus:opacity-100"
          aria-label={isPaused ? "Play announcement animation" : "Pause announcement animation"}
          title={isPaused ? "Play" : "Pause"}
        >
          {isPaused ? (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} key="play">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            </motion.div>
          ) : (
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} key="pause">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            </motion.div>
          )}
        </button>
      </div>

      {/* Main Header */}
      <header className="sticky top-0 z-50 bg-[#FDFBF7] border-b-2 border-black">
        <ScrollProgressBar themeColor={themeColor} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Mobile menu button */}
            <button
              data-testid="mobile-menu-btn"
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
              data-cursor={mobileMenuOpen ? "CLOSE" : "MENU"}
            >
              {mobileMenuOpen ? <X size={24} aria-hidden="true" /> : <Menu size={24} aria-hidden="true" />}
            </button>

            {/* Logo */}
            {/* On houseoflollipop.com, "/" is the waitlist (a separate app served
                by a different origin), so the logo must force a real page
                navigation there instead of client-side routing to this app's
                own home route. */}
            {typeof window !== 'undefined' && window.location.hostname === 'houseoflollipop.com' ? (
              <a href="/" className="flex-shrink-0 group" data-testid="logo-link" data-cursor="HOME">
                <motion.div
                  whileHover={{
                    scale: 1.1,
                    rotate: [0, -5, 5, -5, 0],
                    transition: { duration: 0.5 }
                  }}
                  whileTap={{ scale: 0.9 }}
                  className="logo-text"
                >
                  <img src={LollipopLogo} className="h-6 sm:h-8 w-auto" alt="House of Lollipop Home" />
                </motion.div>
              </a>
            ) : (
              <Link to="/" className="flex-shrink-0 group" data-testid="logo-link" data-cursor="HOME">
                <motion.div
                  whileHover={{
                    scale: 1.1,
                    rotate: [0, -5, 5, -5, 0],
                    transition: { duration: 0.5 }
                  }}
                  whileTap={{ scale: 0.9 }}
                  className="logo-text"
                >
                  <img src={LollipopLogo} className="h-6 sm:h-8 w-auto" alt="House of Lollipop Home" />
                </motion.div>
              </Link>
            )}

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => {
                const isActive = location.pathname + location.search === link.path;
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                    data-cursor={link.label.split(' ')[0]}
                    className={`nav-link font-body text-sm uppercase tracking-wider font-medium py-2 flex items-center gap-1.5 transition-colors duration-200 ${
                      isActive ? 'active text-black' : 'text-gray-500 hover:text-black'
                    }`}
                  >
                    <motion.span
                      animate={isActive ? { scale: 1.05 } : { scale: 1 }}
                      className="relative"
                    >
                      {link.label}
                    </motion.span>
                    {isActive && (
                      <motion.span
                        layoutId="active-nav-dot"
                        className="w-1.5 h-1.5 rounded-full bg-black"
                        initial={{ scale: 0, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ type: "spring", stiffness: 500, damping: 30 }}
                      />
                    )}
                  </Link>
                );
              })}
            </nav>

            {/* Right Icons */}
            <div className="flex items-center gap-4">
              {/* Currency Selector */}
              <motion.div
                key={currency}
                initial={{ scale: 1 }}
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 0.3 }}
              >
                <select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                  className="p-2 border-2 border-black bg-white font-body text-xs font-bold hover:bg-gray-100 transition-colors cursor-pointer focus:ring-2 focus:ring-black outline-none"
                  data-cursor="CASH"
                  aria-label="Change currency"
                >
                  <option value="USD">$ USD</option>
                  <option value="GBP">£ GBP</option>
                </select>
              </motion.div>

              <motion.button
                whileHover={{ scale: 1.1, rotate: -5 }}
                whileTap={{ scale: 0.9 }}
                onClick={() => setSearchOpen(true)}
                data-testid="search-btn" 
                className="p-2 hover:bg-black hover:text-white transition-all duration-200 rounded-lg"
                aria-label="Search products"
                data-cursor="FIND"
              >
                <Search size={20} aria-hidden="true" />
              </motion.button>

              {isAuthenticated ? (
                <motion.div whileHover={{ scale: 1.1, rotate: 5 }} whileTap={{ scale: 0.9 }}>
                  <Link
                    to="/account"
                    data-testid="account-btn"
                    className="p-2 hover:bg-black hover:text-white transition-colors block"
                    aria-label="Account"
                    data-cursor="ME"
                  >
                    <User size={20} aria-hidden="true" />
                  </Link>
                </motion.div>
              ) : (
                <motion.div whileHover={{ scale: 1.1, rotate: 5 }} whileTap={{ scale: 0.9 }}>
                  <Link
                    to="/login"
                    data-testid="login-btn"
                    className="p-2 hover:bg-black hover:text-white transition-colors block"
                    aria-label="Login or Register"
                    data-cursor="GO"
                  >
                    <User size={20} aria-hidden="true" />
                  </Link>
                </motion.div>
              )}

              <motion.div whileHover={{ scale: 1.1, rotate: -5 }} whileTap={{ scale: 0.9 }}>
                <Link
                  to="/account/wishlist"
                  data-testid="wishlist-btn"
                  className="p-2 hover:bg-black hover:text-white transition-colors relative block"
                  aria-label={`View Wishlist, ${wishlist.length} items`}
                  data-cursor="LOVE"
                >
                  <Heart size={20} aria-hidden="true" />
                  <AnimatePresence>
                    {wishlist.length > 0 && (
                      <motion.span
                        key="wishlist-count"
                        initial={shouldReduceMotion ? { opacity: 1 } : { scale: 0 }}
                        animate={shouldReduceMotion ? { opacity: 1 } : { scale: 1 }}
                        exit={shouldReduceMotion ? { opacity: 0 } : { scale: 0 }}
                        className="absolute -top-1 -right-1 w-5 h-5 bg-[#FFC1CC] border border-black rounded-full text-xs flex items-center justify-center font-bold"
                        aria-hidden="true"
                      >
                        {wishlist.length}
                      </motion.span>
                    )}
                  </AnimatePresence>
                  <span className="sr-only" aria-live="polite">
                    {wishlist.length} items in wishlist
                  </span>
                </Link>
              </motion.div>

              <motion.button
                whileHover={{ scale: 1.1, rotate: 5 }}
                whileTap={{ scale: 0.9 }}
                data-testid="cart-btn"
                onClick={() => setCartOpen(true)}
                className="p-2 hover:bg-black hover:text-white transition-colors relative"
                aria-label={`Open Cart, ${itemCount} items`}
                data-cursor="CART"
              >
                <motion.div
                  key={itemCount}
                  animate={itemCount > 0 && !shouldReduceMotion ? {
                    rotate: [0, -20, 20, -20, 0],
                    transition: { duration: 0.5 }
                  } : {}}
                >
                  <ShoppingBag size={20} aria-hidden="true" />
                </motion.div>
                <AnimatePresence>
                  {itemCount > 0 && (
                    <motion.span
                      key="cart-count"
                      initial={shouldReduceMotion ? { opacity: 1 } : { scale: 0 }}
                      animate={shouldReduceMotion ? { opacity: 1 } : { scale: [1, 1.2, 1] }}
                      exit={shouldReduceMotion ? { opacity: 0 } : { scale: 0 }}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-[#00FFFF] border border-black rounded-full text-xs flex items-center justify-center font-bold"
                      aria-hidden="true"
                    >
                      {itemCount}
                    </motion.span>
                  )}
                </AnimatePresence>
                <span className="sr-only" aria-live="polite">
                  {itemCount} items in cart
                </span>
              </motion.button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              id="mobile-navigation"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden border-t-2 border-black overflow-hidden"
            >
              <nav className="flex flex-col py-4 px-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className="py-3 font-body text-lg uppercase tracking-wider font-medium border-b border-gray-200 last:border-0"
                  >
                    {link.label}
                  </Link>
                ))}
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/account"
                      onClick={() => setMobileMenuOpen(false)}
                      className="py-3 font-body text-lg uppercase tracking-wider font-medium border-b border-gray-200"
                    >
                      My Account
                    </Link>
                    <button
                      onClick={() => {
                        logout();
                        setMobileMenuOpen(false);
                      }}
                      className="py-3 font-body text-lg uppercase tracking-wider font-medium text-left text-red-500"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to="/login"
                    onClick={() => setMobileMenuOpen(false)}
                    className="py-3 font-body text-lg uppercase tracking-wider font-medium"
                  >
                    Login / Register
                  </Link>
                )}
              </nav>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      {/* Main Content */}
      <main id="main-content" className="flex-1 focus:outline-none" tabIndex="-1">
        {children}
      </main>

      {/* Recently Viewed - Only show on non-product pages */}
      {!location.pathname.startsWith('/product/') && (
        <Suspense fallback={null}>
          <RecentlyViewed />
        </Suspense>
      )}

      {/* Footer */}
      <footer className="bg-black text-white footer-pattern" style={{ minHeight: '500px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="md:col-span-2">
              <div className="mb-4">
                <img src={LollipopLogo} className="h-8 w-auto invert" alt="Lollipop" />
              </div>
              <p className="font-body text-gray-400 mb-6 max-w-md h-[4.5rem] leading-6">
                Curated vintage tees from {brandNames || 'iconic brands'}. {brands.length || 3} brand{brands.length !== 1 ? 's' : ''}, one love for retro fashion.
                Sustainable style from the vault since 2010.
              </p>
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{
                  visible: {
                    transition: {
                      staggerChildren: 0.1
                    }
                  }
                }}
                className="flex gap-4 h-[3rem] items-center"
              >
                {brands.map(brand => (
                  <motion.div
                    key={brand.slug}
                    variants={{
                      hidden: { opacity: 0, scale: 0, rotate: -20 },
                      visible: {
                        opacity: 1,
                        scale: 1,
                        rotate: 0,
                        transition: { type: 'spring', stiffness: 300, damping: 15 }
                      }
                    }}
                  >
                    <Link
                      to={`/shop?brand=${brand.slug}`}
                      className="w-12 h-12 rounded-full flex items-center justify-center text-black font-bold text-xs border-2 border-black hover:scale-110 transition-transform cursor-pointer sticker-shadow"
                      style={{ backgroundColor: brand.colors.primary }}
                      title={`Shop ${brand.name}`}
                      data-cursor={brand.name.toUpperCase()}
                    >
                      {brand.name.slice(0, 3).toUpperCase()}
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            </div>

            {/* Shop */}
            <div>
              <h3 className="font-popsicle text-lg font-bold uppercase tracking-wider mb-4">Shop</h3>
              <ul className="space-y-3 font-body text-gray-400 min-h-[12rem]">
                <li><Link to="/shop" data-cursor="ALL" className="hover:text-white transition-colors">All Products</Link></li>
                {brands.map(brand => (
                  <li key={brand.slug}>
                    <Link to={`/shop?brand=${brand.slug}`} data-cursor={brand.name.toUpperCase()} className="hover:text-white transition-colors">
                      {brand.name}
                    </Link>
                  </li>
                ))}
                <li><Link to="/shop?featured=true" data-cursor="HOT" className="hover:text-white transition-colors">Featured</Link></li>
              </ul>
            </div>

            {/* Info */}
            <div>
              <h3 className="font-popsicle text-lg font-bold uppercase tracking-wider mb-4">Info</h3>
              <ul className="space-y-3 font-body text-gray-400">
                <li><Link to="/about" data-cursor="US" className="hover:text-white transition-colors">About Us</Link></li>
                <li><Link to="/contact" data-cursor="HI" className="hover:text-white transition-colors">Contact</Link></li>
                <li><span className="cursor-not-allowed">Shipping & Returns</span></li>
                <li><span className="cursor-not-allowed">Size Guide</span></li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="font-body text-gray-500 text-sm">
              © {new Date().getFullYear()} lollipop. All rights reserved.
            </p>
            <p className="font-body text-gray-500 text-sm flex items-center gap-1 group/heart">
              Made With POP
              <motion.span
                whileHover={{ scale: 1.3, rotate: [0, -10, 10, -10, 0] }}
                animate={shouldReduceMotion ? {} : {
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 1.5,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="inline-block mx-1"
              >
                <Heart size={14} className="fill-[#FFC1CC] text-[#FFC1CC]" aria-hidden="true" />
              </motion.span>
              <span className="sr-only">with love</span>
            </p>
          </div>
        </div>
      </footer>

      {/* Cart Drawer */}
      <Suspense fallback={null}>
        <CartDrawer />
      </Suspense>

      {/* Search Overlay */}
      <Suspense fallback={null}>
        <SearchOverlay isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
      </Suspense>

      {/* Back to Top Button with Progress Ring */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.5, rotate: -45 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            exit={{ opacity: 0, scale: 0.5, rotate: 45 }}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 z-[100] p-4 bg-[#FFF44F] border-2 border-black shadow-[4px_4px_0px_0px_#000000] hover:shadow-[6px_6px_0px_0px_#000000] transition-shadow flex items-center justify-center"
            aria-label="Back to top"
            data-cursor="UP"
          >
            <div className="absolute inset-0 p-0.5">
              <svg className="w-full h-full rotate-[-90deg]">
                <circle
                  cx="50%"
                  cy="50%"
                  r="45%"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  className="text-black/10"
                />
                <motion.circle
                  cx="50%"
                  cy="50%"
                  r="45%"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeDasharray="100"
                  style={{
                    pathLength: scrollYProgress,
                    color: "black"
                  }}
                />
              </svg>
            </div>
            <ArrowUp size={24} aria-hidden="true" className="relative z-10" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
};
